import { z } from 'zod';
import { CRM_MODULES, type CrmModule } from '../enums.js';
import { conditionTreeSchema } from '../workflows/conditions.js';
import { paginationQuerySchema, uuidSchema } from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef } from './records.js';

/**
 * Saved list views.
 *
 * A view is a saved *filter*: the same AND/OR condition tree a workflow rule
 * builder emits, plus the columns and sort a list screen shows. Running it is
 * just applying it as the list's own query — there is no separate "run" step
 * the way a report has one, because a list is already a query.
 *
 * Exactly one of `module`/`customModuleId` names what the view filters,
 * mirroring `SavedReport` — a standard CRM module or a tenant-defined one, and
 * never neither or both.
 */

export const savedViewColumnSchema = z.string().trim().min(1).max(120);

const savedViewCore = {
  name: z.string().trim().min(1).max(120),
  module: z.enum(CRM_MODULES).optional(),
  customModuleId: uuidSchema.optional(),
  /** The condition tree; `{}` (the schema's own empty-object case) matches everything. */
  filters: conditionTreeSchema,
  columns: z.array(savedViewColumnSchema).max(30),
  sortBy: z.string().trim().min(1).max(120).nullish(),
  sortDir: z.enum(['asc', 'desc']),
  isShared: z.boolean(),
  isDefault: z.boolean(),
  businessManagerId: uuidSchema.optional(),
};

export const createSavedViewSchema = z
  .object({
    ...savedViewCore,
    filters: conditionTreeSchema.default({}),
    columns: z.array(savedViewColumnSchema).max(30).default([]),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
    isShared: z.boolean().default(false),
    isDefault: z.boolean().default(false),
  })
  .refine((value) => Boolean(value.module) !== Boolean(value.customModuleId), {
    message: 'Name one module',
    path: ['module'],
  });
export type CreateSavedViewInput = z.infer<typeof createSavedViewSchema>;

/**
 * Everything but which module a view filters can be changed after the fact —
 * repointing a saved view at a different module would leave its filters and
 * columns naming fields that may not even exist there, so that is a new view,
 * not an edit of this one.
 */
export const updateSavedViewSchema = z
  .object({
    name: savedViewCore.name,
    filters: savedViewCore.filters,
    columns: savedViewCore.columns,
    sortBy: savedViewCore.sortBy,
    sortDir: savedViewCore.sortDir,
    isShared: savedViewCore.isShared,
    isDefault: savedViewCore.isDefault,
    businessManagerId: savedViewCore.businessManagerId,
  })
  .partial();
export type UpdateSavedViewInput = z.infer<typeof updateSavedViewSchema>;

export const SAVED_VIEW_SORT_FIELDS = ['createdAt', 'updatedAt', 'name'] as const;

export const listSavedViewsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(SAVED_VIEW_SORT_FIELDS).default('createdAt'),
  module: z.enum(CRM_MODULES).optional(),
  customModuleId: uuidSchema.optional(),
  search: z.string().trim().min(1).max(200).optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
});
export type ListSavedViewsQuery = z.infer<typeof listSavedViewsQuerySchema>;

export interface SavedViewSummary {
  id: string;
  name: string;
  module: CrmModule | null;
  customModuleId: string | null;
  isShared: boolean;
  isDefault: boolean;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface SavedViewDetail extends SavedViewSummary {
  filters: unknown;
  columns: string[];
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
}

/**
 * A filter tree submitted from a list page's own query string.
 *
 * Kept separate from `createSavedViewSchema`'s `filters` field: a list request
 * sends the tree JSON-encoded in one query parameter (there being nowhere
 * else to put structured data on a GET), so this parses and validates that
 * string rather than a parsed body. A `ZodError` either way — bad JSON or a
 * tree that fails the grammar — is what the API's global exception filter
 * already turns into a 400 with field-level details.
 */
const filterQueryParamSchema = z
  .string()
  .transform((raw, ctx) => {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      ctx.addIssue({ code: 'custom', message: 'That filter could not be read.' });
      return z.NEVER;
    }
  })
  .pipe(conditionTreeSchema);

export function parseFilterQueryParam(raw: string | undefined): unknown {
  if (!raw) return undefined;
  return filterQueryParamSchema.parse(raw);
}
